import React, { useEffect, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { SheetBody, SheetFooter } from '@/components/ui/sheet'
import { COMPANY_SIZES, INDUSTRIES, JOB_TITLES, MAJOR_CITIES, REGIONS, TagInput } from '@/features/company-profile'
import { ICP_NAME_MAX_LENGTH, type IcpInput } from '../types'

// Country suggestions derived from the city list ("Austin, USA" → "USA"); the field
// still accepts any free text.
const COUNTRY_SUGGESTIONS = Array.from(
  new Set(MAJOR_CITIES.map((city) => city.split(',').pop()?.trim() ?? city)),
).sort()

type Props = {
  // Prefill — an existing ICP (edit) or a copy of one (duplicate). Null starts blank.
  initial: IcpInput | null
  submitLabel: string
  onSubmit: (input: IcpInput) => Promise<void>
  onCancel: () => void
}

export const IcpForm: React.FC<Props> = ({ initial, submitLabel, onSubmit, onCancel }) => {
  const { currentTheme: t } = useTheme()
  const [name, setName] = useState(initial?.name ?? '')
  const [industries, setIndustries] = useState<string[]>(initial?.industries ?? [])
  const [companySizes, setCompanySizes] = useState<string[]>(initial?.companySizes ?? [])
  const [jobTitles, setJobTitles] = useState<string[]>(initial?.jobTitles ?? [])
  const [countries, setCountries] = useState<string[]>(initial?.geography?.countries ?? [])
  const [regions, setRegions] = useState<string[]>(initial?.geography?.regions ?? [])
  const [cities, setCities] = useState<string[]>(initial?.geography?.cities ?? [])
  const [buyingSignals, setBuyingSignals] = useState<string[]>(initial?.buyingSignals ?? [])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A message about the form as it was (a missing name, a duplicate name) is stale the
  // moment the user changes anything.
  useEffect(() => {
    setError(null)
  }, [name, industries, companySizes, jobTitles, countries, regions, cities, buyingSignals])

  const labelClass = 'block text-sm font-semibold mb-2'
  const inputStyle = { backgroundColor: t.inputBackground, color: t.text, borderColor: t.border }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmed = name.trim()
    if (!trimmed) {
      setError('Give this ICP a name, e.g. “Fintech CTOs”.')
      return
    }
    if (industries.length === 0 && companySizes.length === 0 && jobTitles.length === 0) {
      setError('Add at least one industry, company size or job title.')
      return
    }

    setSaving(true)
    try {
      await onSubmit({
        name: trimmed,
        industries,
        companySizes,
        jobTitles,
        geography: { countries, regions, cities },
        buyingSignals,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save. Please try again.')
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <SheetBody className="space-y-5">
        <div>
          <label htmlFor="icp-name" className={labelClass} style={{ color: t.text }}>
            Name <span style={{ color: t.accent }}>*</span>
          </label>
          <input
            id="icp-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, ICP_NAME_MAX_LENGTH))}
            placeholder="e.g. Fintech CTOs"
            className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none"
            style={inputStyle}
            autoFocus
          />
          <p className="mt-1.5 text-xs" style={{ color: t.mutedText }}>
            Zeph shows this name when it asks which customer segment to target.
          </p>
        </div>

        <div>
          <label className={labelClass} style={{ color: t.text }}>Industries</label>
          <TagInput value={industries} onChange={setIndustries} suggestions={INDUSTRIES} placeholder="e.g. SaaS / Software" />
        </div>
        <div>
          <label className={labelClass} style={{ color: t.text }}>Company sizes</label>
          <TagInput value={companySizes} onChange={setCompanySizes} suggestions={COMPANY_SIZES} placeholder="e.g. 11–50" />
        </div>
        <div>
          <label className={labelClass} style={{ color: t.text }}>Job titles</label>
          <TagInput value={jobTitles} onChange={setJobTitles} suggestions={JOB_TITLES} placeholder="e.g. Head of Sales" />
        </div>

        <div>
          <p className={labelClass} style={{ color: t.text }}>
            Geography <span className="font-normal" style={{ color: t.mutedText }}>(optional)</span>
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: t.secondrytext }}>Countries</label>
              <TagInput value={countries} onChange={setCountries} suggestions={COUNTRY_SUGGESTIONS} placeholder="e.g. India" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: t.secondrytext }}>Regions</label>
              <TagInput value={regions} onChange={setRegions} suggestions={REGIONS} placeholder="e.g. EMEA" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: t.secondrytext }}>Cities</label>
              <TagInput value={cities} onChange={setCities} suggestions={MAJOR_CITIES} placeholder="e.g. Bengaluru, India" />
            </div>
          </div>
        </div>

        <div>
          <label className={labelClass} style={{ color: t.text }}>
            Buying signals <span className="font-normal" style={{ color: t.mutedText }}>(optional)</span>
          </label>
          <TagInput value={buyingSignals} onChange={setBuyingSignals} placeholder="e.g. Recently raised funding" />
          <p className="mt-1.5 text-xs" style={{ color: t.mutedText }}>
            Events that suggest a company is ready to buy — press Enter to add each one.
          </p>
        </div>
      </SheetBody>

      <SheetFooter className="flex-wrap">
        {error && (
          <p className="w-full text-sm" role="alert" style={{ color: t.errorText }}>{error}</p>
        )}
        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 h-10 px-5 rounded-lg text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: t.accentGradient ?? t.accent, color: t.buttonText }}
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
          {saving ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-10 px-4 rounded-lg text-sm font-medium disabled:opacity-60"
          style={{ color: t.text, border: `1px solid ${t.border}` }}
        >
          Cancel
        </button>
      </SheetFooter>
    </form>
  )
}

export default IcpForm
